import assert from "assert";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetGeometry, SheetSVGGeometry, StyledSheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { SVG_UNITS_TO_INCHES } from "common/constants/pdfSettings";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { radianToDegree } from "common/utils/math";
import { numMax } from "common/utils/nominalNumbers";
import { objectMergeCascade } from "common/utils/objectMergeCascade";
import type { Mat3 } from "geom/mat3";
import {
  mat3Copy,
  mat3Dot,
  mat3ExtractRotationAngleFrom,
  mat3ExtractScaleVectorFrom,
  mat3Mult,
  mat3Rotate,
  mat3Scale,
} from "geom/mat3";
import { polyLine2TransformCopy } from "geom/polyLine2";
import { PDFTextMetricsProvider } from "pdf/pdfTextMetricsProvider";
import { modelSpaceToPaperSpaceTransformWithPlanRotation } from "../pdf/drawingLayout";
import type { DrawingLayout } from "./layout";
import styles from "./planStyles";
import { normalizeTextBox } from "./primitives/textPrimitive";

export interface TransformWithComponents {
  transform: Mat3;
  transformScale: [number, number];
  transformRotation: number; // radians
}

export function mat3TransformWithComponents(transform: Mat3): TransformWithComponents {
  const transformScale = mat3ExtractScaleVectorFrom(transform);

  // Relative error tolerance of 0.0001
  assert(
    Math.abs(transformScale[0]) - Math.abs(transformScale[1]) <
      (Math.abs(transformScale[0]) + Math.abs(transformScale[1]) + 0.00000001) * 0.0001,
  );

  const transformRotation = mat3ExtractRotationAngleFrom(transform, transformScale);

  return {
    transform,
    transformScale: [transformScale[0], transformScale[1]],
    transformRotation: transformRotation ?? 0,
  };
}

/**
 * The goal of this function is to ultimately be the source of truth for transformations
 * being applied to plan geometry before they are exported to a page.
 *
 * We ultimately want this to be the ONLY place that we are applying page related tranforms
 */
export function sheetGeometryTransform<T extends SheetGeometry>(
  geom: T,
  transformWithComponents: TransformWithComponents,
  normalizeTextRotation = true,
): T {
  // individual transform components = these should probably be stored adjacent to the tranform matrix
  // so that they can accessed if needed
  const { transform, transformScale, transformRotation } = transformWithComponents;
  // const transformTranslation = not used currently

  switch (geom.type) {
    case SheetGeometryType.TEXT: {
      const { rotationBasis } = geom;
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      if (normalizeTextRotation) {
        return normalizeTextBox(
          {
            ...geom,
            x,
            y,
          },
          rotationBasis === "SHEET" ? 0 : transformRotation,
        ) as T;
      }
      const { rotation = 0 } = geom;
      return {
        ...geom,
        x,
        y,
        rotation: rotation + transformRotation,
      };
    }
    case SheetGeometryType.ASSET: {
      // Assets apply transformations to geometry at render time.
      if (geom.transform) {
        return {
          ...geom,
          transform: mat3Mult(transform, geom.transform),
        };
      } else {
        return {
          ...geom,
          transform: mat3Copy(transform),
        };
      }
    }
    case SheetGeometryType.SVG: {
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      let path = geom.path;
      if (transformRotation !== 0) {
        path = `
          <g
            transform="rotate(${radianToDegree(transformRotation)})"
          >${geom.path}</g>
        `;
      }
      return {
        ...geom,
        path,
        x,
        y,
        scale: geom.scale * (geom.inPlanScale ? transformScale[0] : 1),
      };
    }
    case SheetGeometryType.POLYLINE: {
      return {
        ...geom,
        paths: geom.paths.map((p) => polyLine2TransformCopy(p, transform)),
      };
    }
    case SheetGeometryType.POLYGON: {
      return {
        ...geom,
        paths: geom.paths.map((p) => p.map((l) => polyLine2TransformCopy(l, transform))),
      };
    }

    case SheetGeometryType.CIRCLE: {
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      const radius = geom.radius * transformScale[0];
      return {
        ...geom,
        x,
        y,
        radius,
      };
    }
    case SheetGeometryType.ARC: {
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      const xRadius = geom.xRadius * transformScale[0];
      const yRadius = (geom.yRadius ?? geom.xRadius) * transformScale[1];
      // Start and end flip when the y coordinate flips
      const startRad = (transformScale[1] < 0 ? -geom.startRad : geom.startRad) + transformRotation;
      const endRad = (transformScale[1] < 0 ? -geom.endRad : geom.endRad) + transformRotation;
      const clockwise = transformScale[1] < 0 ? !geom.clockwise : geom.clockwise;
      return {
        ...geom,
        x,
        y,
        xRadius,
        yRadius,
        startRad,
        endRad,
        clockwise,
      };
    }
    default: {
      assertUnreachable(geom);
    }
  }
}

export function sheetGeometryYFlipTransform(scaleFactor = 1, rotationRads = 0): TransformWithComponents {
  const transform = mat3Mult(mat3Rotate(rotationRads), mat3Scale(scaleFactor, -scaleFactor));

  return {
    transform,
    transformScale: [scaleFactor, -scaleFactor],
    transformRotation: rotationRads,
  };
}

export function sheetGeometryDrawingLayoutTransform(
  layout: DrawingLayout,
  planRotationRads: number = 0,
): TransformWithComponents {
  const transform = modelSpaceToPaperSpaceTransformWithPlanRotation(layout, planRotationRads);

  // individual transform components = these should probably be stored adjacent to the tranform matrix
  // so that they can accessed if needed
  const transformRotation = planRotationRads;
  const transformScale: [number, number] = [layout?.scaleFactor ?? 1, -(layout?.scaleFactor ?? 1)];
  return {
    transform,
    transformScale,
    transformRotation,
  };
}

export function sheetSVGGeometryScale(geom: SheetSVGGeometry): number {
  return (geom.inPlanScale ? 1 : SVG_UNITS_TO_INCHES) * geom.scale;
}

export function isEditingAnnotation(
  geometry: SheetGeometry,
  editingEntityHandle: DocEntityHandle | undefined,
): boolean {
  if (geometry.type !== SheetGeometryType.TEXT) {
    return false;
  }

  if (!geometry.handle || !editingEntityHandle) {
    return false;
  }

  if (geometry.handle.type === "ANNOTATION" && editingEntityHandle.type === "ANNOTATION") {
    return geometry.handle.id === editingEntityHandle.id;
  }

  return false;
}

export function maybeOverrideEditingAnnotation(
  geometry: SheetGeometry,
  editingEntityHandle: DocEntityHandle | undefined,
  editingEntityProps: Record<string, any> | undefined,
) {
  if (isEditingAnnotation(geometry, editingEntityHandle)) {
    assertIsNotUndefined(editingEntityProps);
    const overrideGeom = objectMergeCascade([styles.text, geometry, editingEntityProps]) as StyledSheetTextGeometry;
    const measuredHeight = PDFTextMetricsProvider.instance.heightOfString(overrideGeom);
    const boxHeight = overrideGeom.boxHeight ? numMax(measuredHeight, overrideGeom.boxHeight) : measuredHeight;
    return { ...overrideGeom, boxHeight };
  }
  return geometry;
}
